import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";
import { Button } from "@/components/ui/button";

import roomOverview from "@/assets/room-overview.webp";
import roomKitchen from "@/assets/room-kitchen.webp";
import roomKitchen2 from "@/assets/room-kitchen-2.webp";
import roomBedroom from "@/assets/room-bedroom.webp";
import roomBathroom from "@/assets/room-bathroom.webp";

const images = [
  { src: roomOverview, alt: "Full room overview with bed, kitchenette, and workspace", label: "Room Overview" },
  { src: roomKitchen, alt: "Full kitchenette with microwave, sink, and refrigerator", label: "Kitchenette" },
  { src: roomKitchen2, alt: "Kitchen area with ample cabinet storage", label: "Kitchen Area" },
  { src: roomBedroom, alt: "Comfortable bedroom with natural light and TV", label: "Bedroom" },
  { src: roomBathroom, alt: "Modern bathroom with walk-in shower", label: "Bathroom" },
];

interface RoomGalleryProps {
  variant?: "hero" | "grid";
}

const RoomGallery = ({ variant = "grid" }: RoomGalleryProps) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const nextImage = () => setSelectedImage((prev) => (prev + 1) % images.length);
  const prevImage = () => setSelectedImage((prev) => (prev - 1 + images.length) % images.length);

  if (variant === "hero") {
    return (
      <div className="relative">
        {/* Main Featured Image */}
        <div 
          className="relative h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-card-hover cursor-pointer group"
          onClick={() => setIsLightboxOpen(true)}
          role="button"
          tabIndex={0}
          aria-label={`View ${images[selectedImage].label} in full screen`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsLightboxOpen(true);
            }
          }}
        >
          <img
            src={images[selectedImage].src}
            alt={images[selectedImage].alt}
            width={800}
            height={600}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-harbor-blue/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
          <Button
            variant="outline"
            size="icon"
            className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              setIsLightboxOpen(true);
            }}
            aria-label="Expand image to full screen"
          >
            <Expand className="w-5 h-5" aria-hidden="true" />
          </Button>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
            <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg font-medium text-harbor-blue text-sm">
              {images[selectedImage].label}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                className="bg-white/90 backdrop-blur-sm w-10 h-10"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="View previous room image"
              >
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="bg-white/90 backdrop-blur-sm w-10 h-10"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="View next room image"
              >
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>

        {/* Thumbnail Strip */}
        <div className="flex gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide" role="tablist" aria-label="Room gallery thumbnails">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImage(index)}
              role="tab"
              aria-selected={selectedImage === index}
              aria-label={`View ${image.label}`}
              className={`relative flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                selectedImage === index
                  ? "ring-2 ring-warm-gold ring-offset-2 scale-105"
                  : "opacity-60 hover:opacity-100"
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                width={96}
                height={64}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {isLightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={() => setIsLightboxOpen(false)}
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 text-white hover:bg-white/10 w-12 h-12"
                onClick={() => setIsLightboxOpen(false)}
                aria-label="Close image lightbox"
              >
                <X className="w-6 h-6" aria-hidden="true" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="View previous image"
              >
                <ChevronLeft className="w-8 h-8" aria-hidden="true" />
              </Button>
              
              <motion.img
                key={selectedImage}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                src={images[selectedImage].src}
                alt={images[selectedImage].alt}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="View next image"
              >
                <ChevronRight className="w-8 h-8" aria-hidden="true" />
              </Button>

              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" role="tablist" aria-label="Image navigation">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(index);
                    }}
                    role="tab"
                    aria-selected={selectedImage === index}
                    aria-label={`View image ${index + 1} of ${images.length}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      selectedImage === index ? "bg-warm-gold w-8" : "bg-white/50 hover:bg-white"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Grid variant for smaller displays
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" role="region" aria-label="Room photo gallery">
      {images.map((image, index) => (
        <button
          key={index}
          type="button"
          aria-label={`View ${image.label} in full screen`}
          className={`relative rounded-xl overflow-hidden cursor-pointer group text-left ${
            index === 0 ? "col-span-2 row-span-2" : ""
          }`}
          onClick={() => {
            setSelectedImage(index);
            setIsLightboxOpen(true);
          }}
        >
          <img
            src={image.src}
            alt={image.alt}
            width={index === 0 ? 600 : 300}
            height={index === 0 ? 384 : 192}
            loading="lazy"
            className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${
              index === 0 ? "h-80 md:h-96" : "h-40 md:h-48"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-harbor-blue/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
          <span className="absolute bottom-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-md text-sm font-medium text-harbor-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
            {image.label}
          </span>
        </button>
      ))}

      {/* Lightbox */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setIsLightboxOpen(false)}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/10 w-12 h-12"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Close image lightbox"
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              aria-label="View previous image"
            >
              <ChevronLeft className="w-8 h-8" aria-hidden="true" />
            </Button>
            
            <motion.img
              key={selectedImage}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={images[selectedImage].src}
              alt={images[selectedImage].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 w-12 h-12"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              aria-label="View next image"
            >
              <ChevronRight className="w-8 h-8" aria-hidden="true" />
            </Button>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" role="tablist" aria-label="Image navigation">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(index);
                  }}
                  role="tab"
                  aria-selected={selectedImage === index}
                  aria-label={`View image ${index + 1} of ${images.length}`}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    selectedImage === index ? "bg-warm-gold w-8" : "bg-white/50 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoomGallery;
